import { useEffect, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import {
  DataGridPro,
  GridActionsCellItem,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid-pro";
import { Popconfirm, message } from "antd";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { EditIcon } from "lucide-react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, red } from "@mui/material/colors";

import AddCommissionModal from "../components/AddCommission";
import CommissionTopBar from "../components/CommisionsTopBar";
import useAxiosInstance from "../helpers/useAxiosInstance";
import {

  Select,
  MenuItem,
  FormControl,
  InputLabel,

} from "@mui/material";
import useStore from "../store/store";
import { hasAdminPriviledge } from "../helpers/accessHelper";

const Commission = () => {
  const axiosInstance = useAxiosInstance()
  const [messageApi, contextHolder] = message.useMessage();

  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [currentRow, setCurrentRow] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 100 });

  const [commissionType, setCommissionType] = useState("aubree")
  const [entityOptions, setEntityOptions] = useState([]);

  const [summary, setSummary] = useState({
    records: 0,
    summary: null,
  });
  const [filters, setFilters] = useState({
    search: "",
    filterType: "all",
    selectedMonthYear: null,
    // paid: false,
  });

  const user = useStore((state) => state.user);
  const isAdmin = hasAdminPriviledge(user);

  // for agent, do not allow UI-driven entity switching
  const effectiveCommissionType = useMemo(() => {
    return isAdmin ? commissionType : null;
  }, [isAdmin, commissionType]);

  useEffect(() => {
    fetchCommissionEntities();
  }, []);

  useEffect(() => {
    fetchData();
  }, [pagination, sortModel, filters, commissionType]);

  const fetchCommissionEntities = async () => {
    try {
      const res = await axiosInstance.get("/commission/entities");
      const options = Array.isArray(res?.data?.data) ? res.data.data : [];

      setEntityOptions(options);

      // default selected entity only for admin
      if (isAdmin && options.length > 0 && !commissionType) {
        setCommissionType(options[0]);
      }
    } catch (error) {
      console.error("Error fetching commission entities:", error);
      setEntityOptions([]);
    }
  };

  const handleAddSubmit = () => {
    setOpenAddModal(false);
    setCurrentRow(null);
    fetchData();
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }).format(new Date(date));
  };

  const handleEdit = (data) => {
    
    setCurrentRow(data);
    setOpenAddModal(true);
  };

  const fetchData = async () => {
    setLoading(true);
    const { search, filterType, selectedMonthYear } = filters;

    const params = {
      page: pagination.page + 1,
      limit: pagination.pageSize,
      sortField: sortModel[0]?.field || "created_at",
      sortOrder: sortModel[0]?.sort || "desc",
      search,
      filterType,
      selectedMonthYear: selectedMonthYear
        ? dayjs(selectedMonthYear).format("YYYY-MM")
        : null,
      entity : commissionType  
      // paid:paid ?? false,
    };

    // send entity only for admin
    if (isAdmin && effectiveCommissionType) {
      params.entity = effectiveCommissionType;
    }

    try {
      const res = await axiosInstance.get(
        `/commission/get-commission-list`,
        { params }
      );
      setCommissions(res?.data?.data || []);
      setTotal(res.data.total || 0);
      setSummary({
        records: res?.data?.total || 0,
        summary: res?.data?.summary || null,
      });
    } catch (err) {
      setCommissions([]);
      setTotal([]);
      setSummary({
        records: 0,
        summary: null,
      });
      console.error("Error fetching commissions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (params) => {
    if (!params) {
      messageApi.warning("Invalid item selected for deletion.");
      return;
    }
    try {
      const res = await axiosInstance.delete(
        `/commission/delete-commission/${params}`
      );

      if (res?.data?.success) {
        messageApi.success("Commission deleted successfully.");
        fetchData();
      } else {
        messageApi.error(
          res?.data?.message || "Something went wrong. Please try again."
        );
      }
    } catch (error) {
      console.error("Delete error:", error);
      messageApi.error("Failed to delete commission. Please try again later.");
    }
  };

  const tableColumns = [
    {
      field: "full_name",
      headerName: "Client Name",
      minWidth: 150,
      flex: 1,
      editable: true,
    },
    {
      field: "invoice_number",
      headerName: "Invoice #",
      minWidth: 120,
      flex: 1,
      editable: true,
    },
    {
      field: "property_name",
      headerName: "Property",
      minWidth: 150,
      flex: 1,
      editable: true,
    },
    {
      field: "move_in_date",
      headerName: "Move In Date",
      valueFormatter: (params) => formatDate(params?.value),
      minWidth: 150,
      flex: 1,
      // editable: true,
    },
    {
      field: "application_date",
      headerName: "Application Date",
      valueFormatter: (params) => formatDate(params?.value),
      minWidth: 150,
      flex: 1,
    },
    {
      field: "lease_term",
      headerName: "Lease Term",
      minWidth: 120,
      flex: 1,
      editable: true,
    },
    {
      field: "lease_end_date",
      headerName: "Lease End Date",
      valueFormatter: (params) => formatDate(params?.value),
      minWidth: 150,
      flex: 1,
      // editable: true,
    },
    {
      field: "renewal_reach_date",
      headerName: "Renewal Reach Date",
      minWidth: 150,
      flex: 1,
      // editable: true,
      valueFormatter: (params) => formatDate(params?.value),
    },
    {
      field: "unit",
      headerName: "Unit",
      minWidth: 120,
      flex: 1,
      editable: true,
    },
    { field: "referral", headerName: "Referral", minWidth: 120, flex: 1 },
    {
      field: "rent",
      headerName: "Rent",
      minWidth: 120,
      flex: 1,
      editable: true,
      renderCell: (params) => {
        const value = params?.value ?? 0;
        return <span>${Number(value)?.toFixed(2) || ""}</span>;
      },
    },
    {
      field: "commission_type",
      headerName: "Commission Type",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "commission",
      headerName: "Commission",
      minWidth: 120,
      flex: 1,
      editable: true,
      renderCell: (params) => {
        const SymbolType = params.row.commission_type
       const Symbol = SymbolType === "percentage" ? '%' : '$'
        const value = params?.value ?? 0;
        return <span>{Symbol}{Number(value)?.toFixed(2) || ""}</span>;
      },
    },
    {
      field: "gross_commission",
      headerName: "Total Commision",
      minWidth: 120,
      flex: 1,
      editable: true,
      renderCell: (params) => {
        const value = params?.value ?? 0;
        return <span>${Number(value)?.toFixed(2) || ""}</span>;
      },
    },
    // {
    //   field: "net_commission",
    //   headerName: "My Commission",
    //   minWidth: 120,
    //   flex: 1,
    // },
    {
      field: "net_commission",
      headerName: "My Commission",
      minWidth: 120,
      flex: 1,
      editable: true,
      renderCell: (params) => {
        const isPaid = params?.row?.paid || false;
        const value = params?.value ?? 0;

        return (
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-medium ${
                isPaid ? "text-green-600" : "text-red-600"
              }`}
            >
              ${Number(value)?.toFixed(2) || 0}
            </span>
            <span
              className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                isPaid
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {isPaid ? "Paid" : "Unpaid"}
            </span>
          </div>
        );
      },
    },
    {
      field: "paid",
      headerName: "Amnt Received",
      minWidth: 120,
      flex: 1,
      editable: true,
      renderCell: (params) =>
        params.value ? (
          <CheckCircleIcon style={{ color: green[600] }} />
        ) : (
          <CancelIcon style={{ color: red[500] }} />
        ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      valueFormatter: (params) => formatDate(params?.value),
      minWidth: 150,
      flex: 1,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      valueFormatter: (params) => formatDate(params?.value),
      minWidth: 150,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon color="#2D68C4" />}
          label="Edit"
          onClick={() => handleEdit(params?.row)}
        />,
        <GridActionsCellItem
          icon={
            <Popconfirm
              title="Are you sure?"
              onConfirm={() => handleDelete(params?.id)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteIcon style={{ color: "red" }} />
            </Popconfirm>
          }
          label="Delete"
          showInMenu={false}
        />,
      ],
    },
  ];

  const handleProcessRowUpdate = async (newRow) => {
    try {
      console.log({ newRow });
      setCommissions((prevRows) =>
        prevRows.map((row) =>
          row?.id === newRow?.id ? { ...row, ...newRow } : row
        )
      );
      const {
        application_date: applicationDate,
        commission,
        commission_type: commissionType,
        full_name: fullName,
        invoice_number: invoiceNumber,
        lease_end_date: leaseEndDate,
        lease_term: leaseTerm,
        move_in_date: moveInDate,
        paid,
        property_name: propertyName,
        referral,
        rent,
        unit,
      } = newRow;

      const payload = {
        applicationDate,
        commission,
        commissionType,
        fullName,
        invoiceNumber,
        leaseEndDate,
        leaseTerm,
        moveInDate,
        paid,
        propertyName,
        referral,
        rent,
        unit,
      };

      const url = `/commission/update-commission/${newRow.id}`;
      await axiosInstance.put(url, payload);
      return newRow;
    } catch (error) {
      console.error("Failed to update the commission row:", error);
    }
  };

  const handleEntityChange = async(event)=>{
    const value = event?.target?.value || "";
    setCommissionType(value)
    }

  return (
    <div className="p-3 rounded-md bg-white">
    <div className="flex justify-between items-center pb-1.5">
      <div>
        <Typography variant="h6" gutterBottom>
          Commissions
        </Typography>
      </div>
        {isAdmin && (
          <div>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-type-label">Entity</InputLabel>
              <Select
                labelId="filter-type-label"
                value={commissionType}
                label="Entity"
                onChange={handleEntityChange}
                className="capitalize"
              >
                {entityOptions.map((option) => (
                  <MenuItem className="capitalize" key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

    </div>


      <CommissionTopBar
        setOpen={setOpenAddModal}
        summary={summary}
        setFilters={setFilters}
        fetchData={fetchData}
      />

      <div className="shadow-md rounded-sm h-[70vh]">
        <DataGridPro
          columns={tableColumns}
          rows={commissions}
          initialState={{
            pinnedColumns: {
              left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
              right: ["actions"],
            },
          }}
          rowCount={total}
          loading={loading}
          // density="compact"
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          getRowId={(row) => row.id}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
          }
          experimentalFeatures={{ newEditingApi: true }}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={(error) => {
            console.error("Error during row update:", error);
            message.error("Something went wrong while saving changes.");
          }}
          disableRowSelectionOnClick
          disableMultipleRowSelection
          sx={{
            "& .MuiDataGrid-columnSeparator": {
              visibility: "visible !important",
            },
            // "& .MuiDataGrid-root": {
            //   overflow: "hidden !important",
            // },
            // "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            //   display: "none",
            // },
          }}
        />
      </div>

      <AddCommissionModal
        open={openAddModal}
        setOpen={setOpenAddModal}
        onClose={() => setOpenAddModal(false)}
        handleAddSubmit={handleAddSubmit}
        messageApi={messageApi}
        currentRow={currentRow}
        setCurrentRow={setCurrentRow}
        entity = {commissionType}
      />
      {contextHolder}
    </div>
  );
};

export default Commission;
